import React, { type JSX, createRef } from "react";
// import { FilesIcon } from "@vector-im/compound-design-tokens/assets/web/icons";
import { Icon as FilesIcon } from "../../../../res/customIcons/file-icon.svg";

import { _t } from "../../../languageHandler";
import BaseDialog from "./BaseDialog";
import DialogButtons from "../elements/DialogButtons";
import { fileSize } from "../../../utils/FileUtils";
import { EmojiButton } from "../rooms/EmojiButton";

interface IProps {
    file: File;
    currentIndex: number;
    totalFiles: number;
    onFinished: (uploadConfirmed: boolean, uploadAll?: boolean, caption?: string) => void;
}

interface IState {
    objectUrl?: string;
    caption: string;
}

export default class UploadConfirmDialog extends React.Component<IProps, IState> {
    private captionRef = createRef<HTMLTextAreaElement>();

    public static defaultProps: Partial<IProps> = {
        totalFiles: 1,
        currentIndex: 0,
    };

    public constructor(props: IProps) {
        super(props);

        this.state = { caption: "" };
    }

    public componentDidMount(): void {
        if (this.props.file.type.startsWith("image/") || this.props.file.type.startsWith("video/")) {
            this.setState({
                // We do not filter the mimetype using getBlobSafeMimeType here as if the user is uploading the file
                // themselves they should be trusting it enough to open/load it, and it will be rendered into a hidden
                // canvas for thumbnail generation anyway
                objectUrl: URL.createObjectURL(this.props.file),
            });
        }
    }

    public componentWillUnmount(): void {
        if (this.state.objectUrl) URL.revokeObjectURL(this.state.objectUrl);
    }

    private onCancelClick = (): void => {
        this.props.onFinished(false);
    };

    private onUploadClick = (): void => {
        this.props.onFinished(true, false, this.state.caption.trim() || undefined);
    };

    private onUploadAllClick = (): void => {
        this.props.onFinished(true, true, this.state.caption.trim() || undefined);
    };

    private onCaptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
        this.setState({ caption: e.target.value });
    };

    private onCaptionKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            this.onUploadClick();
        }
    };

    private addEmoji = (emoji: string): boolean => {
        const textarea = this.captionRef.current;
        if (textarea) {
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const before = this.state.caption.substring(0, start);
            const after = this.state.caption.substring(end);
            const newCaption = before + emoji + after;
            this.setState({ caption: newCaption }, () => {
                // Restore cursor position after the inserted emoji
                const cursorPos = start + emoji.length;
                textarea.focus();
                textarea.setSelectionRange(cursorPos, cursorPos);
            });
        } else {
            this.setState({ caption: this.state.caption + emoji });
        }
        return true;
    };

    public render(): React.ReactNode {
        let title: string;
        if (this.props.totalFiles > 1 && this.props.currentIndex !== undefined) {
            title = _t("upload_file.title_progress", {
                current: this.props.currentIndex + 1,
                total: this.props.totalFiles,
            });
        } else {
            title = _t("upload_file.title");
        }

        const fileId = `mx-uploadconfirmdialog-${this.props.file.name}`;
        const mimeType = this.props.file.type;

        let preview: JSX.Element | undefined;
        // let placeholder: JSX.Element | undefined;
        if (mimeType.startsWith("image/")) {
            preview = (
                <img
                    className="mx_UploadConfirmDialog_imagePreview"
                    src={this.state.objectUrl}
                    aria-labelledby={fileId}
                />
            );
        } else if (mimeType.startsWith("video/")) {
            preview = (
                <video
                    className="mx_UploadConfirmDialog_imagePreview"
                    src={this.state.objectUrl}
                    playsInline
                    controls={false}
                />
            );
        } else {
            // placeholder = <FilesIcon className="mx_UploadConfirmDialog_fileIcon" height="24" width="24" />;
            preview = <FilesIcon className="mx_UploadConfirmDialog_fileIcon" height="24" width="24" />;
        }

        let uploadAllButton: JSX.Element | undefined;
        if (this.props.currentIndex + 1 < this.props.totalFiles) {
            uploadAllButton = <button onClick={this.onUploadAllClick}>{_t("upload_file.upload_all_button")}</button>;
        }

        return (
            <BaseDialog
                className="mx_UploadConfirmDialog"
                fixedWidth={false}
                onFinished={this.onCancelClick}
                title={title}
                contentId="mx_Dialog_content"
            >
                <div id="mx_Dialog_content">
                    <div className="mx_UploadConfirmDialog_previewOuter">
                        <div className="mx_UploadConfirmDialog_previewInner">
                            {preview && <div className="mx_UploadConfirmDialog_previewMedia">{preview}</div>}
                            <div className="mx_UploadConfirmDialog_fileInfo" id={fileId}>
                                <span className="mx_UploadConfirmDialog_fileName">{this.props.file.name}</span>
                                <span className="mx_UploadConfirmDialog_fileSize">{fileSize(this.props.file.size)}</span>
                            </div>
                        </div>
                    </div>
                    <div className="mx_UploadConfirmDialog_captionComposer">
                        <textarea
                            ref={this.captionRef}
                            className="mx_UploadConfirmDialog_captionInput"
                            placeholder={_t("upload_file.caption_placeholder")}
                            value={this.state.caption}
                            onChange={this.onCaptionChange}
                            onKeyDown={this.onCaptionKeyDown}
                            rows={1}
                        />
                        <EmojiButton
                            addEmoji={this.addEmoji}
                            className="mx_UploadConfirmDialog_emojiButton"
                        />
                    </div>
                </div>

                <DialogButtons
                    primaryButton={_t("upload_file.upload_button")}
                    hasCancel={false}
                    onPrimaryButtonClick={this.onUploadClick}
                    focus={true}
                    forceCenter={true}
                >
                    {uploadAllButton}
                </DialogButtons>
            </BaseDialog>
        );
    }
}
